package com.fantasytenis.shared.data.skills.repository

import com.fantasytenis.shared.core.network.ApiResponse
import com.fantasytenis.shared.data.skills.api.SkillsApi
import com.fantasytenis.shared.data.skills.dto.*
import com.fantasytenis.shared.domain.lineup.entity.Category
import com.fantasytenis.shared.domain.skills.entity.*

/**
 * Skills Repository Interface
 */
interface SkillsRepository {
    suspend fun getLeaderboard(
        category: Category,
        gender: Gender? = null,
        limit: Int = 50,
        offset: Int = 0
    ): Result<Pair<List<SkillLeaderboardEntry>, Int>>

    suspend fun getPlayerHistory(
        playerId: String,
        weeks: Int = 8
    ): Result<PlayerSkillHistory>

    suspend fun getTrendingPlayers(
        direction: TrendDirection,
        limit: Int = 10
    ): Result<List<TrendingPlayer>>

    suspend fun getHiddenGems(limit: Int = 10): Result<List<HiddenGemPlayer>>
}

/**
 * Skills Repository Implementation
 */
class SkillsRepositoryImpl(
    private val api: SkillsApi
) : SkillsRepository {

    override suspend fun getLeaderboard(
        category: Category,
        gender: Gender?,
        limit: Int,
        offset: Int
    ): Result<Pair<List<SkillLeaderboardEntry>, Int>> {
        return when (val response = api.getLeaderboard(
            category = category.name.lowercase(),
            gender = gender?.code,
            limit = limit,
            offset = offset
        )) {
            is ApiResponse.Success -> {
                val entries = response.data.players.map { it.toEntity() }
                Result.success(Pair(entries, response.data.total))
            }
            is ApiResponse.Error -> {
                Result.failure(Exception(response.message))
            }
        }
    }

    override suspend fun getPlayerHistory(
        playerId: String,
        weeks: Int
    ): Result<PlayerSkillHistory> {
        return when (val response = api.getPlayerHistory(playerId, weeks)) {
            is ApiResponse.Success -> {
                Result.success(response.data.toEntity())
            }
            is ApiResponse.Error -> {
                Result.failure(Exception(response.message))
            }
        }
    }

    override suspend fun getTrendingPlayers(
        direction: TrendDirection,
        limit: Int
    ): Result<List<TrendingPlayer>> {
        val directionStr = when (direction) {
            TrendDirection.UP -> "up"
            TrendDirection.DOWN -> "down"
            TrendDirection.STABLE -> "up" // Default to up for stable
        }

        return when (val response = api.getTrendingPlayers(directionStr, limit)) {
            is ApiResponse.Success -> {
                Result.success(response.data.map { it.toEntity() })
            }
            is ApiResponse.Error -> {
                Result.failure(Exception(response.message))
            }
        }
    }

    override suspend fun getHiddenGems(limit: Int): Result<List<HiddenGemPlayer>> {
        return when (val response = api.getHiddenGems(limit)) {
            is ApiResponse.Success -> {
                Result.success(response.data.map { it.toEntity() })
            }
            is ApiResponse.Error -> {
                Result.failure(Exception(response.message))
            }
        }
    }
}

// DTO to Entity mapping extensions

private fun SkillLeaderboardEntryDto.toEntity(): SkillLeaderboardEntry {
    return SkillLeaderboardEntry(
        playerId = playerId,
        playerName = playerName,
        country = country,
        atpRanking = atpRanking,
        skillCost = skillCost,
        skillRank = skillRank,
        trend = SkillTrend.fromDelta(skillCost, previousCost),
        gender = Gender.fromCode(gender) ?: Gender.MALE
    )
}

private fun PlayerSkillHistoryDataDto.toEntity(): PlayerSkillHistory {
    return PlayerSkillHistory(
        playerId = playerId,
        playerName = playerName,
        currentCosts = PlayerSkillCosts(
            serve = currentCosts.serve,
            forehand = currentCosts.forehand,
            backhand = currentCosts.backhand,
            volleys = currentCosts.volleys,
            fighting = currentCosts.fighting
        ),
        history = history.map { snapshot ->
            WeeklySkillSnapshot(
                weekOf = snapshot.weekOf,
                costs = PlayerSkillCosts(
                    serve = snapshot.serveCost,
                    forehand = snapshot.forehandCost,
                    backhand = snapshot.backhandCost,
                    volleys = snapshot.volleysCost,
                    fighting = snapshot.fightingCost
                )
            )
        }
    )
}

private fun TrendingPlayerEntryDto.toEntity(): TrendingPlayer {
    return TrendingPlayer(
        playerId = playerId,
        playerName = playerName,
        country = country,
        atpRanking = atpRanking,
        category = Category.fromString(category) ?: Category.SERVE,
        currentCost = currentCost,
        previousCost = previousCost,
        delta = delta
    )
}

private fun HiddenGemEntryDto.toEntity(): HiddenGemPlayer {
    return HiddenGemPlayer(
        playerId = playerId,
        playerName = playerName,
        country = country,
        atpRanking = atpRanking,
        averageCost = averageCost,
        topSkill = TopSkillInfo(
            category = Category.fromString(topSkill.category) ?: Category.SERVE,
            cost = topSkill.cost
        ),
        costs = PlayerSkillCosts(
            serve = costs.serve,
            forehand = costs.forehand,
            backhand = costs.backhand,
            volleys = costs.volleys,
            fighting = costs.fighting
        )
    )
}
